// Mint-Settle: quyết toán tạm ứng theo người (COST-LOGIC mục 5). Dữ liệu mẫu theo code board (tên đã che). EMP: trừ tạm ứng đang giữ trước,
// thiếu → hoàn ứng, thừa → nộp lại; dòng bị từ chối = chưa quyết toán, vẫn trong số đang giữ. Quá hạn quyết toán → chặn ứng mới.

export type SettleGroup = 'fld' | 'doc' | 'drv' | 'cs';
export type SlipState = 'wait' | 'ok' | 'rej';
/** Bản chất dòng chi · chứng từ · người nhận (COST-LOGIC). */
export type SettleNature = 'OBH' | 'BIL' | 'ABS' | 'EXC';
export type SettleDoc = 'INV' | 'PND' | 'RCP' | 'NDC';
/** Dòng chi trong phiếu EMP: chỉ số mã phí (SETTLE_FEES) · phụ · bản chất · chứng từ · tiền · người nhận (chỉ NDC). */
export interface SettleLine { fee: number; sub: string; nat: SettleNature; doc: SettleDoc; amount: number; rcv?: 'IND' | 'PRV' }
/** Phiếu EMP: số · ngày · job / chuyến · trạng thái · các dòng chi. */
export interface SettleSlip { no: string; date: string; job: string; state: SlipState; lines: readonly SettleLine[]; amount: number }
export interface SettlePerson { u: string; seat: string; grp: SettleGroup; photo: boolean; hold: number; due: string; over: number; slips: readonly SettleSlip[] }
export interface SettleCalc { ok: number; wait: number; rej: number; nWait: number; nRej: number; net: number; back: number; ret: number }

/** Mã phí dùng trong quyết toán (`sysServiceCode`; '' = mã chờ cấp, danh mục "Phí hỗ trợ dịch vụ…"). `gov` = người nhận là cơ quan nhà nước. */
export const SETTLE_FEES: readonly { code: string; k: string; gov?: boolean }[] = [
  { code: 'ONN', k: 'Phí nâng cont' }, { code: 'OFF', k: 'Phí hạ cont' }, { code: 'LFE', k: 'Chi phí xe nâng' }, { code: 'LCH', k: 'Chi phí nhân công bốc xếp' },
  { code: 'CLE', k: 'Vệ sinh cont' }, { code: 'SCW', k: 'Chi hộ phí công nhân và điều độ cảng' }, { code: 'PTC', k: 'Phí giám định, kiểm định (có biên lai)', gov: true },
  { code: 'COF', k: 'Chi hộ biên lai chứng thư xuất xứ', gov: true }, { code: 'CUF', k: 'Lệ phí hải quan nộp ngân sách nhà nước', gov: true },
  { code: '', k: 'Phí hỗ trợ dịch vụ hải quan' }, { code: '', k: 'Phí hỗ trợ dịch vụ kiểm dịch' }, { code: '', k: 'Phí hỗ trợ dịch vụ C/O' },
  { code: 'FUE', k: 'Nhiên liệu chuyến' }, { code: 'XLH', k: 'Phí cầu đường' },
];
/** Định mức dòng không chứng từ (NDC) theo mã phí × cảng VNCLI (minh hoạ, chờ KTT chốt). */
const NORM: Readonly<Record<number, number>> = { 3: 500_000, 9: 300_000, 10: 200_000, 11: 150_000 };

const L = (fee: number, sub: string, nat: SettleNature, doc: SettleDoc, amount: number, rcv?: 'IND' | 'PRV'): SettleLine => ({ fee, sub, nat, doc, amount, rcv });
const S = (no: string, date: string, job: string, state: SlipState, lines: SettleLine[]): SettleSlip => ({ no, date, job, state, lines, amount: lines.reduce((a, x) => a + x.amount, 0) });

export const SETTLE_PEOPLE: readonly SettlePerson[] = [
  { u: 'KHA•••', seat: 'OPS-FLD · Hiện trường', grp: 'fld', photo: true, hold: 5_000_000, due: '24/Oct', over: 0, slips: [
    S('ODN2670971', '22/Oct', 'SE260637420', 'wait', [L(0, 'Nâng – ngoài giờ', 'BIL', 'INV', 1_200_000), L(7, 'Biên lai C/O', 'OBH', 'RCP', 300_000), L(9, 'Phí hỗ trợ dịch vụ hải quan', 'ABS', 'NDC', 300_000, 'IND')]),
    S('ODN2670965', '21/Oct', 'SE260635820', 'ok', [L(1, 'Hạ – chuẩn', 'BIL', 'RCP', 1_200_000), L(8, 'Lệ phí hải quan', 'OBH', 'RCP', 400_000)])] },
  { u: 'ANL•••', seat: 'OPS-FLD · Hiện trường', grp: 'fld', photo: false, hold: 3_000_000, due: '19/Oct', over: 4, slips: [
    S('ODN2670957', '19/Oct', 'SE260633750', 'rej', [L(1, 'Hạ – chuẩn', 'BIL', 'RCP', 1_400_000)]),
    S('ODN2670959', '18/Oct', 'SL260635660', 'ok', [L(3, 'Bốc xếp – cá nhân', 'BIL', 'NDC', 600_000, 'IND')])] },
  { u: 'KIE•••', seat: 'OPS-DOC · Chứng từ', grp: 'doc', photo: true, hold: 1_500_000, due: '23/Oct', over: 0, slips: [
    S('ODN2670968', '22/Oct', 'SE260620910', 'ok', [L(7, 'Biên lai C/O', 'OBH', 'RCP', 450_000), L(11, 'Phí hỗ trợ dịch vụ C/O', 'ABS', 'NDC', 150_000, 'PRV')]),
    S('ODN2670969', '22/Oct', 'SE260620910', 'wait', [L(9, 'Phí hỗ trợ dịch vụ hải quan', 'ABS', 'NDC', 450_000, 'IND')])] },
  { u: 'HAI•••', seat: 'TRD-DRV · Tài xế', grp: 'drv', photo: false, hold: 4_000_000, due: '21/Oct', over: 1, slips: [
    S('TU-2610-036', '22/Oct', 'TRF2610078', 'ok', [L(12, 'Dầu DO', 'ABS', 'INV', 1_850_000), L(0, 'Nâng rỗng – chuẩn', 'BIL', 'INV', 286_000)]),
    S('TU-2610-036', '22/Oct', 'TRF2610078', 'rej', [L(13, 'Cầu đường ngoài VETC', 'ABS', 'RCP', 35_000)]),
    S('TU-2610-031', '20/Oct', 'TRF2610051', 'ok', [L(12, 'Dầu DO', 'ABS', 'INV', 1_120_000), L(1, 'Hạ – chuẩn', 'BIL', 'INV', 286_000)]),
    S('TU-2610-040', '22/Oct', 'TRF2610096', 'wait', [L(3, 'Bốc xếp – cá nhân', 'BIL', 'NDC', 300_000, 'IND')])] },
  { u: 'THA•••', seat: 'TRD-DRV · Tài xế', grp: 'drv', photo: false, hold: 1_500_000, due: '21/Oct', over: 1, slips: [S('TU-2610-029', '20/Oct', 'TRF2610045', 'ok', [L(1, 'Hạ – chuẩn', 'BIL', 'INV', 1_406_000)])] },
  { u: 'NHI•••', seat: 'OPS-DOC · Chứng từ', grp: 'doc', photo: true, hold: 0, due: '31/Oct', over: 0, slips: [S('ODN2670972', '22/Oct', 'SE260608050', 'ok', [L(6, 'Kiểm định', 'OBH', 'RCP', 820_000)])] },
  { u: 'PHU•••', seat: 'CS · Dịch vụ khách hàng', grp: 'cs', photo: true, hold: 1_000_000, due: '31/Oct', over: 0, slips: [] },
];

/** Cờ của dòng chi: [nhãn, nghĩa, mức]. Chỉ cờ nghiệp vụ COST-LOGIC (không có luồng chi cán bộ). */
export function lineFlags(l: SettleLine): readonly (readonly [string, string, 'err' | 'warn'])[] {
  const fee = SETTLE_FEES[l.fee];
  const out: [string, string, 'err' | 'warn'][] = [];
  if (l.nat === 'OBH' && l.doc === 'NDC') out.push(['OBH · không chứng từ', 'Chi hộ phải có chứng từ tên khách', 'err']);
  if (fee?.gov && l.doc === 'NDC') out.push(['Cờ KTT', 'Người nhận là cơ quan nhà nước, không có biên lai · chuyển Kế toán trưởng', 'err']);
  const n = l.doc === 'NDC' ? NORM[l.fee] ?? 0 : 0;
  if (n && l.amount > n) out.push(['Vượt định mức', `Định mức ${fee?.k} · VNCLI: ${n.toLocaleString('vi-VN')} ₫ · trưởng bộ phận duyệt${l.nat === 'ABS' ? ' + Kế toán trưởng' : ''}`, 'warn']);
  return out;
}

export const SETTLE_GROUPS: readonly (readonly [SettleGroup | 'all', string])[] = [['all', 'Tất cả bộ phận'], ['fld', 'Hiện trường'], ['doc', 'Chứng từ'], ['drv', 'Tài xế'], ['cs', 'Dịch vụ khách hàng']];

export function settleCalc(p: SettlePerson): SettleCalc {
  const sum = (st: SlipState) => p.slips.filter((x) => x.state === st).reduce((a, x) => a + x.amount, 0);
  const ok = sum('ok'), wait = sum('wait'), rej = sum('rej');
  return {
    ok, wait, rej,
    nWait: p.slips.filter((x) => x.state === 'wait').length,
    nRej: p.slips.filter((x) => x.state === 'rej').length,
    net: Math.min(p.hold, ok), back: Math.max(0, ok - p.hold), ret: Math.max(0, p.hold - ok - wait),
  };
}

export type SettleFilter = 'all' | 'over' | 'wait' | 'due';
const FILTER: Record<SettleFilter, (p: SettlePerson) => boolean> = {
  all: () => true,
  over: (p) => p.over > 0,
  wait: (p) => settleCalc(p).nWait > 0,
  due: (p) => { const c = settleCalc(p); return c.back > 0 || c.ret > 0; },
};
export function filterSettle(grp: SettleGroup | 'all', fil: SettleFilter): SettlePerson[] {
  return SETTLE_PEOPLE.filter((p) => (grp === 'all' || p.grp === grp) && FILTER[fil](p));
}

/** Tiền dạng "1,5 tr" (âm có dấu −) như board. */
export const tr = (x: number): string => `${x < 0 ? '−' : ''}${(Math.abs(x) / 1e6).toFixed(1).replace('.', ',')} tr`;

/** Đối soát số dư trước khoá kỳ: [TK, tên, sổ cái, chi tiết theo phiếu]. */
export const SETTLE_REC: readonly (readonly [string, string, number, number])[] = [
  ['141', 'Tạm ứng', 14_500_000, 14_500_000], ['138', 'Phải thu khác · OBH', 48_600_000, 46_200_000],
  ['331', 'Phải trả người bán', 412_300_000, 412_300_000], ['338', 'Phải trả khác · COL', 18_500_000, 18_500_000],
];

/** Điều kiện chặn khoá kỳ: [mã, tên, số, đơn vị, nguồn]. */
export function closeConditions(): readonly (readonly [string, string, number, string, string])[] {
  const nWait = SETTLE_PEOPLE.reduce((a, p) => a + settleCalc(p).nWait, 0);
  const recBad = SETTLE_REC.filter(([, , gl, sub]) => gl !== sub).length;
  return [
    ['pair', 'Chi hộ / thu hộ chưa cân', 4, 'cặp', 'Chi tiết Job · Costing'],
    ['appr', 'Phiếu chưa duyệt', nWait, 'phiếu', 'Chi phí · Đề nghị thanh toán'],
    ['pnd', 'PND quá hạn', 2, 'dòng', 'Chi phí · chờ hoá đơn'],
    ['dn', 'Debit chưa xuất HĐ', 9, 'debit', 'Hoá đơn · Chờ xuất HĐ'],
    ['rec', 'Số dư TK lệch', recBad, 'TK', 'Đối soát 141 · 138 · 331 · 338'],
  ];
}
